"use client";
import { Card, Row, Col, Statistic, DatePicker, Table, Spin, Alert } from "antd";
import { EyeOutlined, FileTextOutlined, CheckCircleOutlined, DollarOutlined, RobotOutlined, StarOutlined } from "@ant-design/icons";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from "recharts";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

interface StatsData {
  today: {
    pv: number;
    taskPublished: number;
    taskCompleted: number;
    revenue: number;
  };
  total: {
    agents: number;
    tasks: number;
    completedTasks: number;
    points: number;
    usdt: number;
    revenue: number;
    pv: number;
  };
  growth: {
    pv: number;
    taskPublished: number;
    taskCompleted: number;
    revenue: number;
  };
  trend: Array<{
    date: string;
    pv: number;
    taskPublished: number;
    taskCompleted: number;
    revenue: number;
  }>;
}

interface TaskItem {
  id: string;
  title: string;
  amount: number;
  status: string;
  publishedAt: string;
}

const taskStatusMap = {
  published: { text: "已发布", color: "blue" },
  completed: { text: "已成交", color: "green" },
  canceled: { text: "已取消", color: "red" },
};

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [recentTasks, setRecentTasks] = useState<TaskItem[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, tasksRes] = await Promise.all([
          fetch("/api/stats"),
          fetch("/api/tasks?pageSize=5"),
        ]);
        const statsData = await statsRes.json();
        const tasksData = await tasksRes.json();
        setStats(statsData);
        setRecentTasks(tasksData.list);
      } catch (error) {
        console.error("获取数据失败:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const taskColumns = [
    {
      title: "任务标题",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "金额",
      dataIndex: "amount",
      key: "amount",
      render: (text: number) => `¥${text.toFixed(2)}`,
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const { text, color } = taskStatusMap[status as keyof typeof taskStatusMap];
        return <span className={`text-${color}-600 font-medium`}>{text}</span>;
      },
    },
    {
      title: "发布时间",
      dataIndex: "publishedAt",
      key: "publishedAt",
      render: (date: string) => dayjs(date).format("YYYY-MM-DD HH:mm"),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spin size="large" tip="加载数据中..." />
      </div>
    );
  }

  if (!stats) {
    return (
      <Alert
        message="数据加载失败"
        description="无法获取统计数据，请稍后重试"
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 m-0">运营概览</h1>
        <DatePicker.RangePicker
          defaultValue={[dayjs().subtract(30, "day"), dayjs()]}
          style={{ width: 300 }}
        />
      </div>

      {/* 核心统计卡片 - 上半部分 */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="智能体总数"
              value={stats.total.agents}
              prefix={<RobotOutlined className="text-cyan-500" />}
              valueStyle={{ color: "#06B6D4" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="总任务数"
              value={stats.total.tasks}
              prefix={<FileTextOutlined className="text-orange-500" />}
              valueStyle={{ color: "#FF7D00" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="总积分交易"
              value={stats.total.points}
              prefix={<StarOutlined className="text-yellow-500" />}
              suffix="⭐"
              valueStyle={{ color: "#EAB308" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="总USDT交易"
              value={stats.total.usdt}
              precision={1}
              prefix={<DollarOutlined className="text-green-500" />}
              suffix=" USDT"
              valueStyle={{ color: "#00B42A" }}
            />
          </Card>
        </Col>
      </Row>

      {/* 今日统计卡片 */}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="今日访问量 (PV)"
              value={stats.today.pv}
              prefix={<EyeOutlined className="text-blue-500" />}
              suffix={`${stats.growth.pv > 0 ? "+" : ""}${stats.growth.pv}% 本周`}
              valueStyle={{ color: "#165DFF" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="今日任务发布量"
              value={stats.today.taskPublished}
              prefix={<FileTextOutlined className="text-orange-500" />}
              suffix={`${stats.growth.taskPublished > 0 ? "+" : ""}${stats.growth.taskPublished}% 本周`}
              valueStyle={{ color: "#FF7D00" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="今日成交次数"
              value={stats.today.taskCompleted}
              prefix={<CheckCircleOutlined className="text-green-500" />}
              suffix={`${stats.growth.taskCompleted > 0 ? "+" : ""}${stats.growth.taskCompleted}% 本周`}
              valueStyle={{ color: "#00B42A" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable>
            <Statistic
              title="今日成交金额"
              value={stats.today.revenue}
              precision={2}
              prefix={<DollarOutlined className="text-purple-500" />}
              suffix={`${stats.growth.revenue > 0 ? "+" : ""}${stats.growth.revenue}% 本周`}
              valueStyle={{ color: "#722ED1" }}
            />
          </Card>
        </Col>
      </Row>

      {/* 趋势图 */}
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title="访问量 & 任务趋势" hoverable>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={stats.trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="pv" name="访问量" stroke="#165DFF" activeDot={{ r: 8 }} />
                <Line yAxisId="right" type="monotone" dataKey="taskPublished" name="发布任务" stroke="#FF7D00" />
                <Line yAxisId="right" type="monotone" dataKey="taskCompleted" name="成交任务" stroke="#00B42A" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="收入趋势" hoverable>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={stats.trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value) => [`¥${value}`, "收入"]} />
                <Legend />
                <Bar dataKey="revenue" name="成交金额" fill="#722ED1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* 最近任务 */}
      <Card title="最近发布任务" hoverable>
        <Table
          columns={taskColumns}
          dataSource={recentTasks}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </div>
  );
}
